import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Button, Chip, Stack, Typography } from '@mui/material';

import type { MappingChange, SourceRoom } from './types';
import { generateOccupancyText, generateRoomSizeText, generateViewsText } from './util';

interface Props {
  mapping: MappingChange;
  appliedButNotSavedRooms: Array<SourceRoom>;
  onRemoveMapping?: (mapping: MappingChange) => void;
}

export default function ToBeMappedRoomElement({ mapping, appliedButNotSavedRooms, onRemoveMapping }: Props) {
  const getAppliedButNotSavedRoomsItem = (roomId: string) => {
    return appliedButNotSavedRooms.find((room) => room.externalId === roomId);
  };

  const appliedRoomItem = getAppliedButNotSavedRoomsItem(mapping.externalId);

  return (
    <Accordion
      defaultExpanded={false}
      sx={{
        borderColor: 'info.light',
        borderWidth: '2px',
        borderStyle: 'solid',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" width="100%">
          <Stack direction="row" gap={1} alignItems="center">
            <Chip
              label="Pending Save"
              size="small"
              sx={{
                backgroundColor: '#9c27b0',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#7b1fa2',
                },
              }}
            />
            {mapping.isVerified && <Chip label="Verified" color="success" variant="outlined" size="small" />}
            {!mapping.isVerified && <Chip label="Unverified" color="warning" variant="outlined" size="small" />}
            <span>
              ({mapping.supplier}) {getAppliedButNotSavedRoomsItem(mapping.externalId)?.name} - {mapping.externalId}
            </span>
          </Stack>
          {onRemoveMapping && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={(e) => {
                e.stopPropagation(); // Prevent accordion from toggling
                onRemoveMapping(mapping);
              }}
              sx={{ ml: 2 }}
            >
              Remove
            </Button>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Typography>
          <strong>Supplier | External ID: </strong> {`(${mapping.supplier}) ${mapping.externalId}`}
        </Typography>
        <Typography>
          <strong>Views:</strong> {generateViewsText(appliedRoomItem)}
        </Typography>
        <Typography>
          <strong>Room Size: </strong>
          {generateRoomSizeText(appliedRoomItem) || 'N/A'}
        </Typography>
        <Typography>
          <strong>Occupancy Age: </strong>
          {generateOccupancyText(appliedRoomItem).occupancyAge || 'N/A'}
        </Typography>
        <Typography>
          <strong>Occupancy Allowed: </strong>
          {generateOccupancyText(appliedRoomItem).occupancyAllowed || 'N/A'}
        </Typography>

        <Typography>
          <strong>Description: </strong>
          {getAppliedButNotSavedRoomsItem(mapping.externalId)?.description || 'N/A'}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}
